import React, { useEffect, useState, useRef } from 'react';
import { DownloadIcon, MoreHorizontalIcon, MoveHorizontalIcon, MoveVerticalIcon, PrinterIcon, RotateCwIcon, SearchIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
import { InvoiceDocument } from '../types/invoice';
interface ImageViewerPanelProps {
  document: InvoiceDocument;
}
type FitMode = 'none' | 'width' | 'height';
export function ImageViewerPanel({
  document
}: ImageViewerPanelProps) {
  const [fitMode, setFitMode] = useState<FitMode>('width');
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [calculatedScale, setCalculatedScale] = useState(1);
  const showsLineLevelPOs = document.invoiceNumber === 'INV-007';
  useEffect(() => {
    if (fitMode === 'height' && containerRef.current && contentRef.current) {
      requestAnimationFrame(() => {
        if (!containerRef.current || !contentRef.current) return;
        const containerHeight = containerRef.current.clientHeight - 32;
        const contentHeight = contentRef.current.scrollHeight;
        setCalculatedScale(containerHeight / contentHeight);
      });
      return;
    }
    setCalculatedScale(1);
  }, [fitMode, document]);
  const handleFitToWidth = () => {
    setFitMode('width');
    setZoom(100);
  };
  const handleFitToHeight = () => {
    setFitMode('height');
    setZoom(100);
  };
  const handleZoomIn = () => {
    setFitMode('none');
    setZoom(previousZoom => Math.min(previousZoom + 25, 300));
  };
  const handleZoomOut = () => {
    setFitMode('none');
    setZoom(previousZoom => Math.max(previousZoom - 25, 25));
  };
  const handleRotate = () => {
    setRotation(previousRotation => (previousRotation + 90) % 360);
  };
  const getContainerClasses = () => fitMode === 'width' ? 'w-full' : '';
  const getWrapperClasses = () => {
    if (fitMode === 'width') return 'w-full';
    if (fitMode === 'height') return 'h-full flex items-start justify-center py-4';
    return 'flex justify-center';
  };
  const getContentStyle = (): React.CSSProperties => {
    const scale = fitMode === 'height' ? calculatedScale : zoom / 100;
    return {
      transform: `rotate(${rotation}deg) scale(${scale})`,
      transformOrigin: 'center center',
      transition: 'transform 0.2s ease-out'
    };
  };
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col h-full overflow-hidden" data-id="element-6128">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50" data-id="element-6129">
        <div className="flex items-center gap-2" data-id="element-6130">
          <button onClick={handleZoomOut} className="p-1.5 hover:bg-gray-200 rounded transition-colors" title="Zoom Out" aria-label="Zoom out" data-id="element-6131">
            <ZoomOutIcon className="w-4 h-4 text-gray-600" data-id="element-6132" />
          </button>
          <span className="text-xs text-gray-600 font-medium min-w-[3rem] text-center" data-id="element-6133">
            {zoom}%
          </span>
          <button onClick={handleZoomIn} className="p-1.5 hover:bg-gray-200 rounded transition-colors" title="Zoom In" aria-label="Zoom in" data-id="element-6134">
            <ZoomInIcon className="w-4 h-4 text-gray-600" data-id="element-6135" />
          </button>
          <div className="w-px h-6 bg-gray-300 mx-1" data-id="element-6136" />
          <button onClick={handleFitToWidth} className={`px-2.5 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1.5 ${fitMode === 'width' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200 text-gray-700'}`} title="Fit to Width" data-id="element-6137">
            <MoveHorizontalIcon className="w-3.5 h-3.5" data-id="element-6138" />
            Fit Width
          </button>
          <button onClick={handleFitToHeight} className={`px-2.5 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1.5 ${fitMode === 'height' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200 text-gray-700'}`} title="Fit to Height" data-id="element-6139">
            <MoveVerticalIcon className="w-3.5 h-3.5" data-id="element-6140" />
            Fit Height
          </button>
          <div className="w-px h-6 bg-gray-300 mx-1" data-id="element-6141" />
          <button onClick={handleRotate} className="p-1.5 hover:bg-gray-200 rounded transition-colors" title="Rotate" aria-label="Rotate document" data-id="element-6142">
            <RotateCwIcon className="w-4 h-4 text-gray-600" data-id="element-6143" />
          </button>
          <div className="w-px h-6 bg-gray-300 mx-1" data-id="element-6144" />
          <span className="text-sm text-gray-600" data-id="element-6145">1 of 1</span>
        </div>

        <div className="flex items-center gap-2" data-id="element-6146">
          <button className="p-1.5 hover:bg-gray-200 rounded transition-colors" aria-label="Search document" data-id="element-6147">
            <SearchIcon className="w-4 h-4 text-gray-600" data-id="element-6148" />
          </button>
          <button className="p-1.5 hover:bg-gray-200 rounded transition-colors" aria-label="Download document" data-id="element-6149">
            <DownloadIcon className="w-4 h-4 text-gray-600" data-id="element-6150" />
          </button>
          <button className="p-1.5 hover:bg-gray-200 rounded transition-colors" aria-label="Print document" data-id="element-6151">
            <PrinterIcon className="w-4 h-4 text-gray-600" data-id="element-6152" />
          </button>
          <button className="p-1.5 hover:bg-gray-200 rounded transition-colors" aria-label="More document options" data-id="element-6153">
            <MoreHorizontalIcon className="w-4 h-4 text-gray-600" data-id="element-6154" />
          </button>
        </div>
      </div>

      <div ref={containerRef} className="flex-1 bg-gray-100 overflow-auto" data-id="element-6155">
        <div className={`min-h-full ${getWrapperClasses()}`} data-id="element-6156">
          <div ref={contentRef} className={`bg-white shadow-lg ${getContainerClasses()}`} style={getContentStyle()} data-id="element-6157">
            <div className="p-8 border-b border-gray-200" data-id="element-6158">
              <div className="flex justify-between items-start" data-id="element-6159">
                <div data-id="element-6160">
                  <div className="font-bold text-2xl mb-2" data-id="element-6161">
                    {document.supplier}
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-6162">
                    <div data-id="element-6163">123 Supplier Street</div>
                    <div data-id="element-6164">Sydney NSW 2000</div>
                    <div data-id="element-6165">Australia</div>
                    <div className="mt-2" data-id="element-6166">Phone: (02) 9999 8888</div>
                    <div data-id="element-6167">ABN: 12 345 678 901</div>
                  </div>
                </div>

                <div className="text-right" data-id="element-6168">
                  <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6169">
                    INVOICE
                  </div>
                  <div className="text-sm font-medium text-gray-700" data-id="element-6170">
                    {document.invoiceNumber}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8" data-id="element-6171">
              <div className="mb-6 bg-gray-50 p-4 rounded border border-gray-200" data-id="element-6172">
                <div className="text-xs font-bold text-gray-700 mb-2 uppercase" data-id="element-6173">
                  Bill To
                </div>
                <div className="text-sm font-semibold text-gray-900 mb-1" data-id="element-6174">
                  Abbott Australasia Pty Ltd
                </div>
                <div className="text-xs text-gray-600" data-id="element-6175">
                  <div data-id="element-6176">Level 4, 32-34 Lord Street</div>
                  <div data-id="element-6177">Botany NSW 2019</div>
                  <div data-id="element-6178">Australia</div>
                  <div className="mt-2" data-id="element-6179">Phone: (02) 9384 3000</div>
                  <div data-id="element-6180">ABN: 95 000 180 389</div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-6 text-xs" data-id="element-6181">
                <div data-id="element-6182">
                  <div className="text-gray-600 mb-1 font-semibold" data-id="element-6183">
                    Invoice Date
                  </div>
                  <div className="font-medium" data-id="element-6184">{document.invoiceDate}</div>
                </div>
                <div data-id="element-6185">
                  <div className="text-gray-600 mb-1 font-semibold" data-id="element-6186">
                    Due Date
                  </div>
                  <div className="font-medium" data-id="element-6187">{document.dueDate}</div>
                </div>
                <div data-id="element-6188">
                  <div className="text-gray-600 mb-1 font-semibold" data-id="element-6189">Terms</div>
                  <div className="font-medium" data-id="element-6190">Net 30 Days</div>
                </div>
                {document.poNumber && <div data-id="element-6191">
                    <div className="text-gray-600 mb-1 font-semibold" data-id="element-6192">
                      PO Number
                    </div>
                    <div className="font-medium text-blue-600" data-id="element-6193">
                      {document.poNumber}
                    </div>
                  </div>}
              </div>

              <table className="w-full mb-6 text-xs" data-id="element-6194">
                <thead className="bg-gray-100 border-b-2 border-gray-300" data-id="element-6195">
                  <tr data-id="element-6196">
                    <th className="text-left py-2 px-2 font-semibold" data-id="element-6197">
                      Item Code
                    </th>
                    <th className="text-left py-2 px-2 font-semibold" data-id="element-6198">
                      Description
                    </th>
                    {showsLineLevelPOs && <th className="text-left py-2 px-2 font-semibold" data-id="element-6199">PO</th>}
                    <th className="text-right py-2 px-2 font-semibold" data-id="element-6200">Qty</th>
                    <th className="text-right py-2 px-2 font-semibold" data-id="element-6201">
                      Unit Price
                    </th>
                    <th className="text-right py-2 px-2 font-semibold" data-id="element-6202">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-6203">
                  {document.lineItems.map(item => <tr key={item.id} className="border-b border-gray-200" data-id="element-6204">
                      <td className="py-3 px-2 font-mono text-xs" data-id="element-6205">
                        {item.itemCode}
                      </td>
                      <td className="py-3 px-2" data-id="element-6206">{item.description}</td>
                      {showsLineLevelPOs && <td className="py-3 px-2 font-mono text-blue-700 whitespace-nowrap" data-id="element-6207">
                          {item.poMatch?.poNumber ?? '—'}
                        </td>}
                      <td className="text-right py-3 px-2" data-id="element-6208">{item.quantity}</td>
                      <td className="text-right py-3 px-2" data-id="element-6209">
                        ${item.unitPrice.toFixed(2)}
                      </td>
                      <td className="text-right py-3 px-2 font-medium" data-id="element-6210">
                        ${item.total.toFixed(2)}
                      </td>
                    </tr>)}
                </tbody>
              </table>

              <div className="flex justify-end" data-id="element-6211">
                <div className="w-64 text-xs" data-id="element-6212">
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-6213">
                    <span data-id="element-6214">Subtotal:</span>
                    <span className="font-medium" data-id="element-6215">
                      ${document.subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-6216">
                    <span data-id="element-6217">GST (10%):</span>
                    <span className="font-medium" data-id="element-6218">
                      ${document.taxAmt.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-t-2 border-gray-900 font-bold text-base" data-id="element-6219">
                    <span data-id="element-6220">Total {document.currency}:</span>
                    <span data-id="element-6221">${document.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-6222">
                <div className="text-xs" data-id="element-6223">
                  <div className="font-semibold mb-2" data-id="element-6224">Payment Details:</div>
                  <div className="text-gray-600" data-id="element-6225">
                    <div data-id="element-6226">Bank: Commonwealth Bank</div>
                    <div data-id="element-6227">BSB: 062 000</div>
                    <div data-id="element-6228">Account: 1234 5678</div>
                    <div className="mt-2" data-id="element-6229">
                      Please include invoice number in payment reference
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200 text-center" data-id="element-6230">
                <div className="text-xs text-gray-500" data-id="element-6231">
                  Thank you for your business!
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}